/**
 * Browser-side replay anchor client. Intentionally inlined (not imported from
 * `@upg/replay`) so the checkout bundle stays Vite-clean and zero-dep.
 *
 * The vocabulary MUST stay in sync with
 * `packages/core/src/types/evidence.ts::ReplayAnchorKind` — the server
 * rejects unknown kinds. Add a kind in both places at the same time.
 *
 * Privacy: never pass DOM contents, form values, or PII into `attrs`. Only
 * primitives (string | number | boolean | null). The server re-validates and
 * drops anchors that violate this contract — but staying disciplined here
 * keeps client payloads small.
 */

import { baseUrl } from './api.js';
import { classifyFlushResult } from './dom-recorder-policy.js';

/** bd eaxx — drop the anchor batch after this many consecutive network errors. */
const MAX_FLUSH_FAILURES = 5;

export type ReplayAnchorKind =
  | 'session.opened'
  | 'page.viewed'
  | 'method.selected'
  | 'lookup.started'
  | 'lookup.returned'
  | 'lookup.failed'
  | 'consent.accepted'
  | 'consent.revoked'
  | 'duplicate.warning_shown'
  | 'duplicate.warning_acknowledged'
  | 'confirm.clicked'
  | 'redirect.followed'
  | 'return.received'
  | 'session.closed';

type Attrs = Record<string, string | number | boolean | null>;

interface BatchedAnchor {
  kind: ReplayAnchorKind;
  clientTs: string;
  attrs?: Attrs;
}

/**
 * Singleton recorder. The checkout SPA opens at most one risk session per
 * page load, so a singleton matches the runtime shape and keeps the wiring
 * trivial (no React context, no prop drilling).
 */
class CheckoutReplayClient {
  private sessionId: string | null = null;
  private token: string | null = null;
  private queue: BatchedAnchor[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private open = false;
  /**
   * bd eaxx — consecutive network-error retries on flush. Capped by
   * MAX_FLUSH_FAILURES via classifyFlushResult so a flaky connection can't
   * grow the queue unboundedly across the session.
   */
  private consecutiveFailures = 0;

  start(args: { sessionId: string; token: string }): void {
    if (this.open) return;
    this.sessionId = args.sessionId;
    this.token = args.token;
    this.open = true;
    this.mark('session.opened');
    this.timer = setInterval(() => {
      void this.flush();
    }, 3000);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.onPageHide);
    }
  }

  mark(kind: ReplayAnchorKind, attrs?: Attrs): void {
    if (!this.open) return;
    this.queue.push({
      kind,
      clientTs: new Date().toISOString(),
      ...(attrs ? { attrs } : {}),
    });
    if (this.queue.length >= 16) void this.flush();
  }

  async flush(): Promise<void> {
    if (!this.open || !this.sessionId || !this.token || this.queue.length === 0) {
      return;
    }
    const anchors = this.queue;
    this.queue = [];
    const body = JSON.stringify({ session_id: this.sessionId, anchors });
    const useBeacon =
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden';
    if (useBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(
        `/v1/replay/record?token=${encodeURIComponent(this.token)}`,
        blob,
      );
      // sendBeacon path: page is unloading, no opportunity to retry. Accept
      // the loss on a false return (the events are about to be lost anyway).
      this.consecutiveFailures = 0;
      return;
    }
    let networkError = false;
    let httpOk = false;
    try {
      const res = await fetch(`${baseUrl}/v1/replay/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Replay-Token': this.token,
        },
        credentials: 'include',
        body,
        keepalive: true,
      });
      httpOk = res.ok;
    } catch {
      networkError = true;
    }

    const decision = classifyFlushResult({
      networkError,
      httpOk,
      consecutiveFailures: this.consecutiveFailures,
      maxFailures: MAX_FLUSH_FAILURES,
    });
    this.consecutiveFailures = decision.nextFailures;
    if (decision.action === 'requeue') {
      // bd eaxx — bounded re-queue. Server is idempotent on (session, seq).
      this.queue.unshift(...anchors);
    }
    // 'drop' or 'clear' both leave the events un-requeued — they're either
    // server-acknowledged or we've exhausted the retry budget.
  }

  async stop(reason: 'unmount' | 'visibility-hidden' | 'pagehide' = 'unmount'): Promise<void> {
    if (!this.open) return;
    this.mark('session.closed', { reason });
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    await this.flush();
    this.open = false;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.onPageHide);
    }
  }

  private onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      void this.flush();
    }
  };

  private onPageHide = (): void => {
    void this.stop('pagehide');
  };
}

export const replayClient = new CheckoutReplayClient();
